import { api } from '@/api/ApiGateway.js'

class OrganizationsApi
{
	constructor()
	{
		this.filters = []
	}

	/**
	 * Create a new organization record
	 * @param {{organization_name: string, organization_name_dba: string, street: string, city: string, state: string, zip: string, facilty_county: string, ccn: string, tin: string, npi: string }} orgData
	 * @param {function} onSuccess
	 * @param {function} onError
	 */
	create(orgData, onSuccess, onError)
	{
		api.post('organizations/create', {
			'organization': orgData
		}).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error creating organization.' })
				onError(response)
				return
			}

			onSuccess(response.body()?.data)
		})
	}

	/**
	 * Search for organizations.
	 * If CCN is set, it will query for (CCN or NPI or TIN). Other fields will be searched as AND, (CCN...) or STREET and ZIP....
	 * @param {{ccn: string, organization_name: string, street: string, city: string, state: string, zip: string}} searchCriteria Search criteria to use, values ignored if null or empty string. Pass null for numbers to exclude.
	 * @param {string} sortBy DB column to sort by
	 * @param {number} limit Max number of records to return
	 * @param {Array<number>} existingOrgIds Array of org ids to exclude from results
	 * @param {function} onSuccess Function to call when search is successful
	 * @param {function} onError Function to call when an error occurs searching
	 */
	search(searchCriteria, sortBy, limit, existingOrgIds, onSuccess, onError)
	{
		let query = 'organizations/organizations/search';

		//Create a JSON object to define where clause
		//	Note, this is a WiP with an aim to come up with a way to create more complex queries.

		/*
			[
				{
					operator: 'and', // 'and' or 'or': defines the logical operator to use between filter groups (ignored if its th first group)
					filters:
					[
						{
							column: 'ccn', // column in the table to search
							search_type: 'prefix', // Type of where clause search type to use and if '%'s are required. (see portal_app_service.QueryApi.addFilterWhere for options)
							value: 'bloop', // Value to search (can be an array)
							operator: 'or' // Operator between this and the previous filter. 'and' or 'or'
						},
					...]
				},
			...]
		 */

		//If the CCN is set, all other fields are ignored.
		if (searchCriteria['ccn'] && searchCriteria['ccn'].length > 0)
		{
			let filters = [];
			this.addFilter(filters, 'ccn', 'infix', searchCriteria['ccn'], 'or');
			this.addFilter(filters, 'tin', 'infix', searchCriteria['ccn'], 'or');
			this.addFilter(filters, 'npi', 'infix', searchCriteria['ccn'], 'or');
			this.addFilterGroup('and', filters);
		}
		else
		{
			if (this.hasValue(searchCriteria['name']))
			{
				let filters = [];
				this.addFilter(filters, 'organization_name', 'infix', searchCriteria['name'], 'or');
				this.addFilter(filters, 'organization_name_dba', 'infix', searchCriteria['name'], 'or');
				this.addFilterGroup('and', filters);
			}

			let filters = [];
			this.addFilter(filters, 'street', 'infix', searchCriteria['address'], 'and');
			this.addFilter(filters, 'city', 'infix', searchCriteria['city'], 'and');
			this.addFilter(filters, 'state', 'infix', searchCriteria['state'], 'and');
			this.addFilter(filters, 'zip', 'infix', searchCriteria['zip'], 'and');
			this.addFilterGroup('or', filters);
		}

		api.get(query, {
			sort: sortBy,
			limit: limit,
			existingOrgIds: existingOrgIds,
			filter: JSON.stringify(this.filters)
		}).then(response =>
		{
			this.filters = [];
			if (response.hasError())
			{
				onError(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error loading organizations data.' })
				onError(response)
				return
			}

			onSuccess(response.body()?.data)
		})
	}

	/**
	 * Returns organization data for a single record based on ID
	 * @param p_id {Number} ORGANIZATIONS.ID
	 * @param p_on_success {Function} Function to call when successful (Passed two params, array of data and an array of facility_type)
	 * @param p_on_error {Function} Function to call when error
	 */
	single(p_id, p_on_success, p_on_error)
	{
		let query = `organizations/organizations/${p_id}`

		api.get(query).then(response =>
		{
			if (response.hasError())
			{
				p_on_error(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error loading organizations data.' })
				p_on_error(response)
				return
			}
			p_on_success(response.body()?.data, response.body()?.facility_types)
		})
	}

	update(organizationData, onSuccess, onError)
	{
		api.patch('organizations/organizations', organizationData).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error updating user organization.' })
				onError(response)
				return
			}

			onSuccess(response.body()?.data)
		})
	}

	/**
	 * Checks if a variable is not null and not blank
	 * @param p_field Column name
	 * @returns {boolean} True if p_field has a value
	 */
	hasValue(p_field)
	{
		if (typeof p_field === "undefined" || p_field === null)
		{
			return false;
		}
		else if (typeof p_field === 'string' && p_field.length === 0)
		{
			return false;
		}

		return true;
	}

	delete(orgId, onSuccess, onError)
	{
		api.delete(`organizations/organizations/${orgId}`).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			try
			{
				onSuccess(response.body()?.data)
			}
			catch (e)
			{
				response.overrideError({ detail: `${e}` })
				onError(response)
			}
		})
	}

	restore(orgId, onSuccess, onError)
	{
		api.get(`organizations/organizations/restore/${orgId}`).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error restoring user data.' })
				onError(response)
				return
			}

			onSuccess(response.body()?.data)
		})
	}

	/**
	 * Adds a filter group.
	 * @param p_operator 'and' or 'or''
	 * @param p_filters Array of filters to add. Created by addFilter()
	 */
	addFilterGroup(p_operator, p_filters)
	{
		if (p_filters.length > 0)
		{
			this.filters.push(
				{
					'operator': p_operator,
					'filters': p_filters
				}
			);
		}
	}

	/**
	 * Adds a filter to the filter group.
	 * @param p_group Array of filters to add. Created by addFilterGroup()
	 * @param p_column Column name
	 * @param p_search_type 'prefix', 'suffix', etc. (see portal_app_service.QueryApi.addFilterWhere for options)
	 * @param p_value Value
	 * @param p_operator Operator between this and the previous filter. 'and' or 'or'
	 */
	addFilter(p_group, p_column, p_search_type, p_value, p_operator = "and")
	{
		if (this.hasValue(p_value))
		{
			p_group.push({ column: p_column, search_type: p_search_type, value: p_value, operator: p_operator })
		}
	}
}

export const organizationsApi = new OrganizationsApi()